<template lang="pug">
.documents-pdf-viewer
  //- Select
  select.document-select.form-select(
    v-if='loaded && !hideSelector'
    v-model='selectedDocument'
  )
    option(:value='document' v-for='(document, index) in documents') {{ document.name }}
  button.field-focus.btn.start(
    @click='goToNextField'
    v-if='this.getEmptyFields().length > 0 && loaded'
  )
    span.text(v-if='this.getEmptyFields().length === fields.length') {{ $t('modal.envelope.start') }}
    span.text(v-else) {{ $t('modal.envelope.next') }}
  pdf-renderer(
    :key='selectedDocument.id'
    :show-envelope-options='true'
    :src='documentSrc'
    @document-loaded='loaded = $event; $emit("loaded", $event)'
    @download-envelope='downloadEnvelope'
    @download-signatures='downloadSignatures'
    @fullscreen='showFullScreen'
    @proxy-pages='onLoadPages($event)'
    @scale-change='onScaleChange'
    v-if='documentSrc'
  )
    //- Slot of each page <3
    template(
      v-for='(page, index) in pages'
      v-slot:[`page-${page.number}`]='{}'
    )
      //- Render elements: fields and contents
      .elements-list(:key='`${index}-${selectedDocument.id}`')
        component(
          :class='{ "element-field": element.category === "field" }'
          :element='element'
          :global-initial='globalInitial'
          :global-signature='globalSignature'
          :is='isMobile ? "DocumentElementViewerMobile" : "DocumentElementViewer"'
          :key='element.id'
          :page='page'
          :recipientId='recipientId'
          :scale='currentScale'
          :style='element.type === "image" ? { "z-index": -1 } : {}'
          @change='onElementChange'
          @edit-initial='editInitial(element)'
          @edit-signature='editSignature(element)'
          @initial-click='initialClick(element)'
          @signature-click='signatureClick(element)'
          v-for='(element, index) in page.elements'
        )
  //- Signature modal
  signature-creator-modal#signature-creator-modal(
    :default-text='signatureModalDefaultText'
    :label='signatureModalLabel'
    :signature-support='signatureSupport'
    :signature-text-max-length='signatureModalTextLength'
    @change-signature='onSignatureUpdate'
    ref='signatureCreatorModal'
  )
</template>
<script>
import PdfRenderer from '@/ui/molecules/PdfRenderer.vue';
import SignatureCreatorModal from '@/ui/organisms/modals/SignatureCreatorModal.vue';
import DocumentElementViewer from '@/ui/molecules/DocumentElementViewer.vue';
import DocumentElementViewerMobile from '@/ui/molecules/DocumentElementViewerMobile.vue';
import Icon from '@/ui/atoms/Icon.vue';
import EnvelopeState from '@/enums/EnvelopeState';
import alert from '@/modules/account/common/alert';
import Abbreviator from '@/services/Abbreviator';
import EnvelopeDownloadsFactory from '@/modules/business/common/EnvelopeDownloads/EnvelopeDownloadsFactory';

export default {
  name: 'DocumentsPDFViewer',
  components: {
    PdfRenderer,
    SignatureCreatorModal,
    DocumentElementViewer,
    DocumentElementViewerMobile,
    Icon,
  },
  props: {
    documents: Array,
    document: {
      type: Object,
      default: () => {},
    },
    recipientId: String,
    envelope: {
      type: [Object, String],
      default: null,
    },
    signatureSupport: {
      type: Array,
    },
    hideSelector: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loaded: false,
      pages: [],
      clickedElement: '',
      documentSrc: null,
      selectedDocument: {},
      signatureModalLabel: this.$t('modal.envelope.fields.signature'),
      signatureModalTextLength: 30,
      signatureModalDefaultText: null,
      globalSignature: '',
      globalInitial: '',
      documentAutoSelect: false,
      currentScale: 1,
      overlapTimeout: null,
      overlapWarningShow: true,
    };
  },
  mounted() {
    if (this.document) {
      this.selectedDocument = this.document;
      this.documentSrc = this.selectedDocument.src;
    }
    this.$emit('empty-fields', this.getEmptyFields());
  },
  watch: {
    document(document) {
      this.selectedDocument = document;
    },
    selectedDocument(document) {
      this.documentSrc = null;
      this.page = 1;
      this.pages = [];
      this.loaded = false;
      setTimeout(() => {
        this.documentSrc = document.src;
        this.resetTopOnFocusButton();
      }, 100);
      this.$emit('document-selected', document);
    },
    loaded(value) {
      if (value) {
        this.showOverlapWarningOnce();
      }
      if (value && this.documentAutoSelect) {
        setTimeout(() => {
          this.documentAutoSelect = false;
          this.goToNextField();
        }, 3000);
      }
    },
  },
  computed: {
    isMobile() {
      return this.$root.isMobile || this.$root.isMobileScreen;
    },
    labelFieldFocus() {
      if (
        this.getEmptyFields().length >
        this.fields.length - this.getEmptyFields().length
      ) {
        return this.$t('modal.envelope.recipients');
      }
      return this.$t('modal.envelope.start');
    },
    fields() {
      // eslint-disable-next-line max-len
      const elements = this.documents.reduce(
        (ret, document) => [].concat(ret, document.elements || []),
        []
      );
      return (
        elements
          .filter(
            (e) => e.category === 'field' && e.recipient.id === this.recipientId
          )
          .sort((a, b) => a.apiY * 100 ** a.page - b.apiY * 100 ** b.page) || []
      );
    },
    hasToken() {
      return this.$route.params.token;
    },
    hasOverlappingElements() {
      const overlaps = [];
      // gel all documents
      for (const doc of this.documents) {
        if (!doc.elements?.length) {
          continue;
        }
        // get fields and contents by actual document
        const fieldsAndContents = doc.elements.filter(
          (e) =>
            ['field', 'content'].includes(e.category) &&
            e.apiX != null &&
            e.apiY != null &&
            e.apiWidth > 0 &&
            e.apiHeight > 0
        );
        // compare pairs of fields and contents
        for (let i = 0; i < fieldsAndContents.length; i++) {
          const el1 = fieldsAndContents[i];
          for (let j = i + 1; j < fieldsAndContents.length; j++) {
            const el2 = fieldsAndContents[j];
            // if different pages, skip
            if (el1.page !== el2.page) {
              continue;
            }
            // other elements can overlap images
            if (el1.type === 'image' || el2.type === 'image') {
              continue;
            }
            const overlap = this.calculateOverlap(el1, el2);
            // overlap > 50%
            if (overlap > this.$root.warningOverlapPercent) {
              overlaps.push({
                overlapping: true,
                document: doc,
                element1: el1,
                element2: el2,
                overlap: overlap,
              });
            }
          }
        }
      }
      return overlaps;
    },
  },
  methods: {
    changeDocumentById(documentId) {
      const document = this.documents
        .filter((d) => d.id === documentId)
        .shift();
      if (!document) {
        return;
      }
      this.selectedDocument = document;
    },
    invalidRecipient(element) {
      if (!element.recipient) {
        return true;
      }
      if (element.recipient.id !== this.recipientId) {
        return true;
      }
      return false;
    },
    editInitial(element) {
      if (this.invalidRecipient(element)) {
        return;
      }
      if (this.$root.user.name) {
        this.signatureModalDefaultText = Abbreviator.get(this.$root.user.name);
      } else {
        this.signatureModalDefaultText = Abbreviator.get(
          element.recipient.addressees[0].name
        );
      }
      this.signatureModalLabel = this.$t('modal.envelope.fields.initial');
      this.signatureModalTextLength = 5;
      if (element.defaultInitial && !element.value) {
        // eslint-disable-next-line no-param-reassign
        element.value = element.defaultInitial;
        // eslint-disable-next-line no-param-reassign
        element.defaultInitial = '';
        this.$emit('element-updated', element);
        this.$emit('empty-fields', this.getEmptyFields());
      } else {
        this.$refs.signatureCreatorModal.$refs.modal.open();
      }
      this.clickedElement = element;
    },
    editSignature(element) {
      if (this.invalidRecipient(element)) {
        return;
      }
      if (this.$root.user.name) {
        this.signatureModalDefaultText = this.$root.user.name;
      } else {
        this.signatureModalDefaultText = element.recipient.addressees[0].name;
      }
      this.signatureModalLabel = this.$t('modal.envelope.fields.signature');
      this.signatureModalTextLength = 30;
      this.$refs.signatureCreatorModal.$refs.modal.open();
      this.clickedElement = element;
    },
    initialClick(element) {
      if (this.invalidRecipient(element)) {
        return;
      }
      if (this.globalInitial) {
        this.clickedElement = element;
        element.value = this.globalInitial;
        this.$emit('element-updated', element);
        this.$emit('empty-fields', this.getEmptyFields());
        return;
      }
      this.editInitial(element);
    },
    signatureClick(element) {
      if (this.invalidRecipient(element)) {
        return;
      }
      if (this.globalSignature) {
        this.clickedElement = element;
        element.value = this.globalSignature;
        this.$emit('element-updated', element);
        this.$emit('empty-fields', this.getEmptyFields());
        return;
      }
      this.editSignature(element);
    },
    getEmptyFields() {
      return (
        this.fields.filter((e) => !e.value && e.required && !e.disabled) || []
      );
    },
    goToNextField() {
      const nextEmptyField = this.getEmptyFields()[0];
      if (!nextEmptyField) {
        return;
      }
      if (nextEmptyField.document !== this.selectedDocument.id) {
        this.documentAutoSelect = true;
        this.changeDocumentById(nextEmptyField.document);
        return;
      }

      const currentFieldElement = document.querySelector(
        `#element-${nextEmptyField.id}`
      );
      const pdfPagesElement = document.querySelector(
        '.pdf-render__content__pages'
      );

      if (!currentFieldElement || !pdfPagesElement) return;

      let incrementPagesSize = 0;
      for (let $c = 1; $c < nextEmptyField.page; $c += 1) {
        const pageElement = pdfPagesElement.querySelector(`#page-${$c}`);
        if (pageElement) {
          incrementPagesSize += pageElement.offsetHeight + 7.5;
        }
      }

      const scrollX =
        currentFieldElement.offsetLeft -
        pdfPagesElement.clientWidth / 2 +
        currentFieldElement.offsetWidth / 2;
      const scrollY =
        currentFieldElement.offsetTop +
        incrementPagesSize -
        pdfPagesElement.clientHeight / 2 +
        currentFieldElement.offsetHeight / 2;

      pdfPagesElement.scrollTo({
        left: scrollX,
        top: scrollY,
        behavior: 'smooth',
      });

      currentFieldElement.classList.add('focus');
      setTimeout(() => {
        currentFieldElement.classList.remove('focus');
      }, 1700);
    },
    getFocusButtonElement() {
      return document.querySelector('a.field-focus');
    },
    setCustomBottomOnFocusButton(bottom) {
      const element = this.getFocusButtonElement();
      if (element) {
        element.style.bottom = `${bottom}px`;
        element.style.top = 'auto';
      }
    },
    resetTopOnFocusButton() {
      const element = this.getFocusButtonElement();
      if (element) {
        element.removeAttribute('style');
      }
    },
    async downloadEnvelope() {
      let id = this.$route.params.token;
      let type = 'public';
      if (!this.hasToken) {
        id = this.envelope.id;
        type = 'private';
      }
      this.$loading(true);
      try {
        const envelopeDownloads =
          EnvelopeDownloadsFactory.createEnvelopeDownloads(id, type);

        await envelopeDownloads.downloadDocuments(this.envelope.title);
      } finally {
        this.$loading(false);
      }
    },
    showFullScreen() {
      window.open(this.selectedDocument.links.display, '_blank');
    },
    downloadSignatures() {
      if (this.envelope.state === EnvelopeState.COMPLETED) {
        window.open(this.selectedDocument.links.background_sheet, '_blank');
        return;
      }
      alert.fireAlert(this.$t('modal.envelope.signaturesPageError'), {
        classes: 'alert-warning',
        styles:
          'background-color: #FFFCE2; border-color: #EBE5A7; color: #635B00;',
        tag: 'httpAlert',
        icon: 'circle-exclamation',
      });
    },
    onLoadPages(pages) {
      this.pages = pages.map((p) => {
        let elements = [];
        if (this.selectedDocument.elements) {
          elements = this.selectedDocument.elements.filter(
            (e) => e.page === p.pageNumber
          );
        }
        return {
          size: {
            width: p.view[2],
            height: p.view[3],
          },
          rotation: p.rotate,
          number: p.pageNumber,
          elements,
        };
      });
    },
    onSignatureUpdate(signature) {
      if (this.clickedElement.type === 'initial') {
        this.globalInitial = signature.image;
      }
      if (this.clickedElement.type === 'signature') {
        this.globalSignature = signature.image;
      }
      this.clickedElement.value = signature.image;
      this.$emit('signature-updated', signature, this.clickedElement);
      this.$emit('empty-fields', this.getEmptyFields());
    },
    onElementChange(e) {
      this.$emit('element-updated', e);
      this.$emit('empty-fields', this.getEmptyFields());
    },
    onScaleChange(scale) {
      this.currentScale = scale;
    },
    processOverlapWarning(message) {
      alert.fireAlert(message, {
        classes: 'alert-warning',
        styles:
          'background-color: #FFFCE2; border-color: #EBE5A7; color: #635B00;',
        tag: 'httpAlert',
        icon: 'circle-exclamation',
      });
    },
    calculateOverlap(el1, el2) {
      // rectangles for calculate overlap
      const left1 = el1.apiX;
      const right1 = left1 + el1.apiWidth;
      const top1 = el1.apiY;
      const bottom1 = top1 + el1.apiHeight;
      const left2 = el2.apiX;
      const right2 = left2 + el2.apiWidth;
      const top2 = el2.apiY;
      const bottom2 = top2 + el2.apiHeight;

      if (
        right1 < left2 ||
        left1 > right2 ||
        bottom1 < top2 ||
        top1 > bottom2
      ) {
        return 0;
      }

      const overlapLeft = Math.max(left1, left2);
      const overlapRight = Math.min(right1, right2);
      const overlapTop = Math.max(top1, top2);
      const overlapBottom = Math.min(bottom1, bottom2);

      const overlapArea =
        (overlapRight - overlapLeft) * (overlapBottom - overlapTop);
      const smallerArea = Math.min(
        el1.apiWidth * el1.apiHeight,
        el2.apiWidth * el2.apiHeight
      );

      return overlapArea / smallerArea;
    },
    groupOverlaps(overlaps) {
      const result = [];
      if (!overlaps.length) {
        return [];
      }
      overlaps.forEach((overlap) => {
        const doc = overlap.document.name;
        const page = overlap.element1.page;
        const exists = result.find(
          (r) => r.doc.name === doc && r.doc.page === page
        );
        if (!exists) {
          result.push({
            doc: {
              name: doc,
              page: page,
            },
          });
        }
      });
      return result;
    },
    formatOverlapResult(result) {
      const grouped = {};
      result.forEach((r) => {
        const n = r.doc.name;
        const p = r.doc.page;
        if (!grouped[n]) grouped[n] = [];
        if (!grouped[n].includes(p)) grouped[n].push(p);
      });
      return Object.entries(grouped)
        .map(([n, p]) => `${n}, pág. ${p.join(',')}`)
        .join('. ');
    },
    showOverlapWarningOnce() {
      if (!this.overlapWarningShow) {
        return;
      }
      const overlaps = this.hasOverlappingElements;
      if (!overlaps.length) {
        return;
      }
      const details = this.formatOverlapResult(this.groupOverlaps(overlaps));
      const message = this.$t('modal.envelope.fields.hasOverlapWarning', {
        details: details,
      });
      this.processOverlapWarning(message);
      this.showDetailedOverlapWarnings(overlaps);
      this.overlapWarningShow = false;
    },
    showDetailedOverlapWarnings(overlaps) {
      if (!overlaps.length) {
        return;
      }
      overlaps.forEach((overlapData, index) => {
        setTimeout(() => {
          const percent = (overlapData.overlap * 100).toFixed(1);
          const field1 = overlapData.element1.type;
          const field2 = overlapData.element2.type;
          const message = this.$t('modal.envelope.fields.overlapWarning', {
            document: overlapData.document.name,
            percent: percent,
            field1: this.$t(`modal.envelope.fields.${field1}`),
            f1P: overlapData.element1.page,
            field2: this.$t(`modal.envelope.fields.${field2}`),
            f2P: overlapData.element2.page,
          });
          console.warn(message);
        }, index * 50);
      });
    },
  },
};
</script>
<style lang="stylus">
.documents-pdf-viewer
  position: relative
  .pdf-thumbs
    height: calc(100vh - 255px)
  .document-select
    margin: auto
    width: 100%
    max-width: 73%
    position: absolute
    z-index: 2
    right: auto
    left: 0.4rem
    top: 0.4rem
    padding: .3em 2.5em .3em .6em
    border-color: #CCCCCC !important
    background-color: #fff
    border-radius: 5px
  .elements-list
    position: absolute
    top: 0
    bottom: 0
    right: 0
    left: 0
    margin: auto
    z-index: 1
.element-field
  z-index: 9
.field-focus
  cursor: pointer
  position: absolute
  top: 10%
  z-index: 2
  background-color: #013D52
  color: #fff
  font-size: .8rem
  text-align: center
  padding: 1em 1em 1.15em 1em
  line-height: 1em
  padding-right: 2.5em
  border-radius: 5px
  min-width: 85px
  clip-path: polygon(0% 0%, 75% 0%, 100% 50%, 75% 100%, 0% 100%);
  &:hover
    text-decoration: none
    color: #fff
    opacity: 1
  > *
    font-size: 1rem
    font-weight: 400
@media screen and (min-width: 468px)
  .documents-pdf-viewer
    .document-select
      max-width: 78%
@media screen and (min-width: 576px)
  .documents-pdf-viewer
    .document-select
      max-width: 40%
      left: 13.5rem
@media screen and (min-width: 768px)
  .documents-pdf-viewer
    .document-select
      max-width: 35%
      left: 14rem
@media screen and (min-width: 992px)
  .documents-pdf-viewer
    .document-select
      max-width: 50%
      left: 14.5rem
@media screen and (min-width: 1400px)
  .documents-pdf-viewer
    .document-select
      max-width: 60%
      left: 14.5rem
@media screen and (max-width: 468px)
  .documents-pdf-viewer
    .document-select
      max-width: 100%
      position: relative
      left: 0
      top: 0
      right: 0
      margin-bottom: 0.8rem
</style>
